import { useEffect, useId, useRef, useState } from 'react';

/**
 * Ícone de informação que revela um texto ao ser acionado.
 *
 * Existe para tirar da tela os blocos de explicação que ocupavam espaço
 * permanente sem serem lidos toda vez. O texto não é descartado: fica a um
 * clique, ANCORADO no elemento que explica. Essa âncora é o ponto — uma nota
 * no rodapé sobre um número que está no topo obriga quem lê a ligar as duas
 * coisas sozinho, e a maioria não liga.
 *
 * O que ele NÃO deve esconder: aviso que muda a leitura do que está na tela.
 * "Suprimido não é zero" ou "este total exclui os sem território" mudam o
 * significado do número ao lado; escondê-los produz leitura errada em vez de
 * tela limpa. Para esses, o lugar é o texto visível, e o `Info` fica com o
 * detalhe de método.
 *
 * Acessibilidade: é um `button`, não um `span` com `onClick` — recebe foco
 * pelo teclado, anuncia-se como botão e abre com Enter ou Espaço sem código
 * extra. O painel referencia o botão por `aria-labelledby` e fecha com Esc.
 */
export function Info({
  titulo,
  children,
  className,
}: {
  /** Frase curta do que o painel explica. Vira o rótulo acessível do botão. */
  titulo: string;
  children: React.ReactNode;
  className?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const id = useId();
  const caixa = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!aberto) return;

    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAberto(false);
    };
    // Clicar fora fecha. Sem isto, abrir um painel e ir embora deixa dois
    // abertos ao mesmo tempo, sobrepostos.
    const aoClicar = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };

    document.addEventListener('keydown', aoTeclar);
    document.addEventListener('mousedown', aoClicar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
      document.removeEventListener('mousedown', aoClicar);
    };
  }, [aberto]);

  return (
    <span ref={caixa} className={`info-wrap${className ? ` ${className}` : ''}`}>
      <button
        type="button"
        id={`${id}-botao`}
        className="info-btn"
        aria-expanded={aberto}
        aria-label={titulo}
        onClick={(e) => {
          // O ícone costuma ficar dentro de cartões clicáveis. Sem isto, abrir
          // a explicação navega para outra tela.
          e.stopPropagation();
          e.preventDefault();
          setAberto((v) => !v);
        }}
      >
        i
      </button>

      {aberto && (
        <span
          role="note"
          aria-labelledby={`${id}-botao`}
          className="info-pop"
          data-testid="info-conteudo"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </span>
      )}
    </span>
  );
}
